import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { useDispatch } from 'react-redux';
import { X, Search, Trash2, Link2, Copy, CheckCircle2, Clock, AlertCircle, Plus, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { fetchOrdersDetailsById } from '../../store/thunk/ordersThunk';
import { mapOrdersToZellePayment, searchOrdersByContact, unmapOrderFromZellePayment } from '../../store/thunk/adminThunk';
import toast from 'react-hot-toast';
import { formatPrice, formatToUSDate } from '../../hooks/commonFunction';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';

const OrderMappingModal = ({ open, setOpen, onApply, zellePaymentDetails }) => {
  const dispatch = useDispatch();
  const [searchOrderId, setSearchOrderId] = useState('');
  const [mappedOrders, setMappedOrders] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isSearchResultsExpanded, setIsSearchResultsExpanded] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const hasAutoSearched = useRef(false);
  const [isUnmapConfirmOpen, setIsUnmapConfirmOpen] = useState(false);
  const [orderToUnmap, setOrderToUnmap] = useState(null);
  const [isUnmapping, setIsUnmapping] = useState(false);

  // Auto-search by name when modal opens and pre-fill search input
  useEffect(() => {
    if (open && zellePaymentDetails?.name && !hasAutoSearched.current) {
      hasAutoSearched.current = true;
      setSearchOrderId(zellePaymentDetails.name);
      handleSearchByName(zellePaymentDetails.name);
    }
    if (!open) {
      hasAutoSearched.current = false;
    }
  }, [open, zellePaymentDetails?.name]);

  // Search orders by contact using search-by-contact API
  const handleSearchByName = async (searchTerm) => {
    if (!searchTerm?.trim()) return;
    setIsLoadingResults(true);
    try {
      const payload = {
        search_term: searchTerm.trim(),
        page: 1,
        limit: 10,
        ...(startDate && { start_date: format(startDate, 'yyyy-MM-dd') }),
        ...(endDate && { end_date: format(endDate, 'yyyy-MM-dd') }),
      };
      const result = await dispatch(searchOrdersByContact(payload)).unwrap();
      if (result?.data?.orders) {
        setSearchResults(result.data.orders);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Failed to search orders:', error);
      toast.error('Failed to search orders');
      setSearchResults([]);
    } finally {
      setIsLoadingResults(false);
    }
  };

  // Calculate total of previously mapped orders (from API)
  const previouslyMappedTotal = (zellePaymentDetails?.mapped_orders || []).reduce((sum, order) => {
    return sum + (parseFloat(order?.applied_amount) || 0);
  }, 0);

  // Calculate total of newly added mapped orders
  const newlyMappedOrdersTotal = mappedOrders.reduce((sum, order) => {
    return sum + (parseFloat(order?.payment_info?.total_paid) || 0);
  }, 0);

  // Combined total (previous + new)
  const mappedOrdersTotal = newlyMappedOrdersTotal;

  // Check if total matches Zelle payment amount (accounting for previous mappings)
  const zelleAmount = parseFloat(zellePaymentDetails?.amount) || 0;
  const availableZelleBalance = zelleAmount - previouslyMappedTotal;
  const isProcessed = zellePaymentDetails?.status === 'processed';
  const isTotalMatched = (newlyMappedOrdersTotal >= availableZelleBalance) || (availableZelleBalance <= 0);
  const remainingAmount = availableZelleBalance - newlyMappedOrdersTotal;

//   console.log(mappedOrders, "mappedOrders")

  // Handle copy memo to clipboard
  const handleCopyMemo = () => {
    if (zellePaymentDetails?.memo) {
      navigator.clipboard.writeText(zellePaymentDetails.memo);
      toast.success('Memo copied to clipboard!');
    }
  };

  // Handle unmap order
  const handleUnmapOrder = async () => {
    if (!orderToUnmap) return;
    setIsUnmapping(true);
    try {
      const payload = {
        zelle_payment_id: zellePaymentDetails?.id,
        payment_id: orderToUnmap.payment_id,
      };
      const result = await dispatch(unmapOrderFromZellePayment(payload)).unwrap();
      if (result?.status === true) {
        const revertedAmount = result?.data?.reverted_amount;
        toast.success(
          result?.message ||
            `Order #${orderToUnmap.payment_id} unmapped successfully` +
              (revertedAmount ? ` — $${revertedAmount} restored` : ''),
        );
        setIsUnmapConfirmOpen(false);
        setOrderToUnmap(null);
        if (onApply) {
          onApply([]); // Trigger a refresh in parent
        }
      } else {
        toast.error(result?.message || 'Failed to unmap order');
      }
    } catch (error) {
      console.error('Failed to unmap order:', error);
      toast.error(typeof error === 'string' ? error : 'Failed to unmap order');
    } finally {
      setIsUnmapping(false);
    }
  };

  // Parse partner info
  const getPartnerInfo = (partnerInfoString) => {
    try {
      if (!partnerInfoString) return null;
      const parsed = JSON.parse(partnerInfoString);
      return parsed?.partner?.[0] || null;
    } catch {
      return null;
    }
  };

  // Handle adding an order from search results
  const handleAddFromResults = async (order) => {
    const orderId = order?.payment_id;
    if (!orderId) {
      toast.error('Invalid order');
      return;
    }

    // Check if Zelle balance is fully allocated
    if (availableZelleBalance <= 0) {
      toast.error('Cannot add more orders. Zelle payment is fully allocated');
      return;
    }

    if (isTotalMatched) {
      toast.error('Cannot add more orders. Total already meets or exceeds available balance');
      return;
    }

    // Check if already mapped
    const orderExists = mappedOrders.some(
      (o) => (o?.payment_info?.payment_id) === orderId
    );
    if (orderExists) {
      toast.error('This order is already added');
      return;
    }

    // Check if already in previously mapped orders
    const previouslyMapped = (zellePaymentDetails?.mapped_orders || []).some(
      (o) => o.payment_id === orderId
    );
    if (previouslyMapped) {
      toast.error('This order is already mapped in a previous mapping');
      return;
    }

    try {
      // Fetch full order details
      const result = await dispatch(fetchOrdersDetailsById(orderId)).unwrap();
      if (result) {
        const newOrderTotal = parseFloat(result?.payment_info?.total_paid) || 0;
        const newTotal = mappedOrdersTotal + newOrderTotal;

        setMappedOrders([...mappedOrders, result]);
        toast.success('Order added successfully');

        if (newTotal === availableZelleBalance) {
          toast.success('Total amount matches available balance!');
        } else if (newTotal > availableZelleBalance) {
          toast('Total exceeds available balance', { icon: '⚠️' });
        }
      } else {
        toast.error('Failed to fetch order details');
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to fetch order details');
    }
  };

  const handleSearchOrder = async (dateOverride = null) => {
    // Determine which dates to use: passed in overrides or current state
    const currentStart = dateOverride?.startDate !== undefined ? dateOverride.startDate : startDate;
    const currentEnd = dateOverride?.endDate !== undefined ? dateOverride.endDate : endDate;

    if (!searchOrderId.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setIsSearching(true);
    try {
      const payload = {
        search_term: searchOrderId.trim(),
        page: 1,
        limit: 10,
        ...(currentStart && { start_date: format(currentStart, 'yyyy-MM-dd') }),
        ...(currentEnd && { end_date: format(currentEnd, 'yyyy-MM-dd') }),
      };
      const result = await dispatch(searchOrdersByContact(payload)).unwrap();
      if (result?.data?.orders) {
        setSearchResults(result.data.orders);
      } else {
        setSearchResults([]);
        toast.error('No orders found');
      }
    } catch (error) {
      console.error('Failed to search orders:', error);
      toast.error('Failed to search orders');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle delete order from list
  const handleDeleteOrder = (paymentId) => {
    setMappedOrders(mappedOrders.filter((order) => order?.payment_info?.payment_id !== paymentId));
    toast.success('Order removed from list');
  };

  // Handle apply mappings
  const handleApply = async () => {
    if (mappedOrders.length === 0) {
      toast.error('Please add at least one order');
      return;
    }

    setIsApplying(true);
    try {
      let remainingBalance = availableZelleBalance;

      // Build the orders array with proper applied amounts and status
      const orders = mappedOrders.map((order, index) => {
        const orderTotal = parseFloat(order?.payment_info?.total_paid) || 0;
        let appliedAmount = 0;
        let status = 'PENDING';

        // Calculate how much of this order can be paid by remaining Zelle balance
        if (remainingBalance > 0) {
          if (remainingBalance >= orderTotal) {
            // Full order can be paid
            appliedAmount = orderTotal;
            status = 'FULLY_PAID';
            remainingBalance -= orderTotal;
          } else {
            // Partial payment (Zelle runs out on this order)
            appliedAmount = remainingBalance;
            status = 'PARTIAL_FUND';
            remainingBalance = 0;
          }
        } else {
          // No Zelle balance left for this order
          appliedAmount = 0;
          status = 'PENDING';
        }

        return {
          payment_id: order?.payment_info?.payment_id,
          order_total: orderTotal,
          applied_amount: appliedAmount,
          status: status,
        };
      });

      // Calculate if this is a partial funding scenario (using available balance, not full amount)
      const isPartial = newlyMappedOrdersTotal !== availableZelleBalance;

      // Determine where the remaining amount is
      let storedRemainingAmount = 0;
      let remainingState = 'COMPLETED';

      if (newlyMappedOrdersTotal > availableZelleBalance) {
        // Orders total more than available balance - some orders have unpaid amounts
        storedRemainingAmount = newlyMappedOrdersTotal - availableZelleBalance;
        remainingState = 'PENDING'; // Remaining order amounts are pending
      } else if (newlyMappedOrdersTotal < availableZelleBalance) {
        // Available balance is more than orders - Zelle has remaining funds
        storedRemainingAmount = availableZelleBalance - newlyMappedOrdersTotal;
        remainingState = 'AVAILABLE'; // Zelle funds available for future use
      }

      // Determine overall payment state
      let paymentState = 'FULLY_PAID';
      const hasPartialOrders = orders.some(order => order.status === 'PARTIAL_FUND');
      const hasPendingOrders = orders.some(order => order.status === 'PENDING');

      if (hasPartialOrders || hasPendingOrders || isPartial) {
        paymentState = 'PARTIAL_FUND';
      }

      const payload = {
        zelle_id: zellePaymentDetails?.id,
        transaction_id: zellePaymentDetails?.transaction_id,
        orders: orders,
        partial_fund: {
          is_partial: isPartial,
          stored_remaining_amount: storedRemainingAmount,
          remaining_state: remainingState,
        },
        final_summary: {
          payment_state: paymentState,
        },
      };

    //   console.log('Mapping payload:', payload);

      // Dispatch the API call
      await dispatch(mapOrdersToZellePayment(payload)).unwrap();

      toast.success('Orders mapped successfully!');
      onApply(mappedOrders);
      handleClose();
    } catch (error) {
      console.error('Failed to map orders:', error);
      toast.error(error || 'Failed to map orders to Zelle payment');
    } finally {
      setIsApplying(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!isSearching) {
      setSearchOrderId('');
      setMappedOrders([]);
      setSearchResults([]);
      setStartDate(null);
      setEndDate(null);
      setIsDateModalOpen(false);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} className='relative z-50'>
      <DialogBackdrop className='fixed inset-0 bg-black/50' />

      <div className='fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto'>
        <DialogPanel className='bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col'>
          {/* Header */}
          <div className='flex items-center justify-between mb-4 sm:mb-6 bg-red-gradient p-3 sm:p-4'>
            <DialogTitle className='text-sm sm:text-md font-normal text-white flex items-center space-x-2 sm:space-x-3'>
              <Link2 className='h-5 w-5 sm:h-6 sm:w-6' />
              <span className='truncate'>Map Orders to Zelle Payment</span>
            </DialogTitle>
            <button
              onClick={handleClose}
              disabled={isSearching}
              className='text-white transition-colors hover:text-gray-200 disabled:opacity-50 flex-shrink-0'
            >
              <X size={20} className='sm:hidden' />
              <X size={24} className='hidden sm:block' />
            </button>
          </div>
            <div className=' overflow-y-scroll'>
          {/* Zelle Payment Details Section */}
          {zellePaymentDetails && (
            <div className='px-3 sm:px-5 mb-4 sm:mb-6'>
              <div className='bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 sm:p-5 shadow-sm'>
                {/* <h3 className='text-sm sm:text-base font-semibold text-gray-800 mb-3 flex items-center gap-2'>
                  <span className='w-2 h-2 bg-blue-600 rounded-full'></span>
                  Zelle Payment Details
                </h3> */}
                <div className='grid grid-cols-4 md:grid-cols-8 gap-3 sm:gap-4'>
                  {/* <div>
                    <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-600 mb-1'>
                      Payment ID
                    </label>
                    <p className='text-sm sm:text-base font-bold text-primary-100'>
                      #{zellePaymentDetails.payment_id || zellePaymentDetails.id}
                    </p>
                  </div> */}
                  <div className='col-span-2'>
                    <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-600 mb-1'>
                      Name
                    </label>
                    <p className='text-sm sm:text-base font-semibold text-gray-900'>
                      {zellePaymentDetails.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-600 mb-1'>
                      Amount
                    </label>
                    <p className='text-sm sm:text-base font-bold text-green-700'>
                      {formatPrice(zellePaymentDetails.amount)}
                    </p>
                  </div>
                  <div>
                    <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-600 mb-1'>
                      Transaction ID
                    </label>
                    <p className='text-xs sm:text-sm font-mono text-primary-100'>
                      {zellePaymentDetails.transaction_id || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-600 mb-1'>
                      Status
                    </label>
                    <span
                      className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium capitalize ${
                        zellePaymentDetails.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : zellePaymentDetails.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {zellePaymentDetails.status || 'pending'}
                    </span>
                  </div>
                  {zellePaymentDetails.memo && (
                    <div className='col-span-3 md:col-span-3'>
                      <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-600 mb-1'>
                        Memo
                      </label>
                      <div className='relative group'>
                        <p className='text-xs md:text-sm text-gray-700'>
                          {zellePaymentDetails.memo}
                        </p>
                        <button
                          onClick={handleCopyMemo}
                          className='absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-primary-100 hover:bg-gray-100 rounded transition-all duration-200'
                          title='Copy memo'
                        >
                          <Copy size={16} className='sm:w-[18px] sm:h-[18px]' />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Existing Mapped Orders Section */}
          {zellePaymentDetails?.mapped_orders && zellePaymentDetails.mapped_orders.length > 0 && (
            <div className='px-3 sm:px-5 mb-4 sm:mb-6'>
              <div className='bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 sm:p-5 shadow-sm'>
                <h3 className='text-sm sm:text-base font-semibold text-gray-800 mb-3 flex items-center gap-2'>
                  <CheckCircle2 className='h-4 w-4 sm:h-5 sm:w-5 text-green-600' />
                  Previously Mapped Orders ({zellePaymentDetails.mapped_orders.length})
                </h3>
                <div className='space-y-3'>
                  {zellePaymentDetails.mapped_orders.map((mappedOrder) => (
                    <div
                      key={mappedOrder.mapping_id}
                      className='bg-white border border-green-100 rounded-lg p-3 sm:p-4 shadow-sm relative group'
                    >
                      {/* Delete button for previously mapped orders */}
                        <button
                          onClick={() => {
                            setOrderToUnmap(mappedOrder);
                            setIsUnmapConfirmOpen(true);
                          }}
                          className='absolute right-2 top-2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200'
                          title='Delete mapping'
                        >
                          <Trash2 size={16} />
                        </button>
                      <div className='grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3'>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                            Order ID
                          </label>
                          <p className='text-sm sm:text-base font-bold text-primary-100'>
                            #{mappedOrder.payment_id}
                          </p>
                        </div>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                            Customer
                          </label>
                          <p className='text-xs sm:text-sm font-medium text-gray-900 truncate'>
                            {mappedOrder.user_name || 'N/A'}
                          </p>
                          {mappedOrder.user_phone && (
                            <p className='text-[10px] sm:text-xs text-gray-500'>
                              {mappedOrder.user_phone}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                            Order Total
                          </label>
                          <p className='text-xs sm:text-sm font-medium text-gray-700'>
                            {formatPrice(mappedOrder.order_total)}
                          </p>
                        </div>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                            Applied Amount
                          </label>
                          <p className='text-sm sm:text-base font-bold text-green-700'>
                            {formatPrice(mappedOrder.applied_amount)}
                          </p>
                        </div>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                            Payment Status
                          </label>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                              mappedOrder.payment_status === 'FULLY_PAID'
                                ? 'bg-green-100 text-green-800'
                                : mappedOrder.payment_status === 'PARTIAL_FUND'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {mappedOrder.payment_status === 'FULLY_PAID' && <CheckCircle2 size={10} />}
                            {mappedOrder.payment_status === 'PARTIAL_FUND' && <Clock size={10} />}
                            {mappedOrder.payment_status === 'PENDING' && <AlertCircle size={10} />}
                            {mappedOrder.payment_status || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                            Mapped At
                          </label>
                          <p className='text-[10px] sm:text-xs text-gray-600'>
                            {formatToUSDate(mappedOrder.mapped_at)}
                          </p>
                        </div>
                      </div>
                      {/* Additional info row */}
                      <div className='grid grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mt-2 pt-2 border-t border-gray-100'>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                            Order Type
                          </label>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                            mappedOrder.order_type === 'delivery-partner'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {mappedOrder.order_type || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                            Delivery Method
                          </label>
                          <p className='text-[10px] sm:text-xs text-gray-600 capitalize'>
                            {mappedOrder.delivery_method || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                            Pickup Date
                          </label>
                          <p className='text-[10px] sm:text-xs text-gray-600'>
                            {formatToUSDate(mappedOrder.pickup_date)}
                          </p>
                        </div>
                        {/* <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                            Order Status
                          </label>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium capitalize ${
                            mappedOrder.order_status === 'completed' || mappedOrder.order_status === 'delivered'
                              ? 'bg-green-100 text-green-800'
                              : mappedOrder.order_status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {mappedOrder.order_status || 'N/A'}
                          </span>
                        </div> */}  
                      </div>
                    </div>
                  ))}
                </div>
                {/* Summary of previously mapped orders */}
                <div className='mt-3 pt-3 border-t border-green-200 flex justify-between items-center'>
                  <span className='text-xs sm:text-sm text-gray-600'>
                    Total Applied from Previous Mappings:
                  </span>
                  <span className='text-sm sm:text-base font-bold text-green-700'>
                    {formatPrice(
                      zellePaymentDetails.mapped_orders.reduce(
                        (sum, order) => sum + (parseFloat(order.applied_amount) || 0),
                        0
                      )
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Search Section - Hide when processed */}
          {!isProcessed && (
          <div className='px-3 sm:px-5 mb-4 sm:mb-6'>
            <div className='flex items-center justify-between mb-2'>
              <label className='block text-xs sm:text-sm letter-spacing-07 font-medium text-gray-700'>
                Search Order
              </label>
              {zelleAmount > 0 && (
                <div className='text-xs sm:text-sm font-medium'>
                  {availableZelleBalance <= 0 ? (
                    <span className='text-gray-500 bg-gray-100 px-2 sm:px-3 py-1 rounded-full'>
                      Fully Allocated
                    </span>
                  ) : isTotalMatched ? (
                    <span className='text-green-600 bg-green-50 px-2 sm:px-3 py-1 rounded-full'>
                      ✓ Total Matched
                    </span>
                  ) : (
                    <span className='text-blue-600'>
                      Available: <span className='font-bold'>{formatPrice(remainingAmount)}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className='flex gap-2 sm:gap-3'>
              <div className='relative flex-1'>
                <input
                  type='text'
                  placeholder={isTotalMatched ? 'Total amount matched' : 'Search by name, phone, order ID...'}
                  value={searchOrderId}
                  onChange={(e) => setSearchOrderId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isTotalMatched) {
                      handleSearchOrder();
                    }
                  }}
                  className={`w-full border-2 py-2.5 sm:py-3 px-3 sm:px-4 pr-10 shadow-sm text-sm focus:outline-none rounded-lg transition-all duration-200 ${
                    isTotalMatched
                      ? 'border-gray-200 bg-gray-100 cursor-not-allowed text-gray-500'
                      : 'border-gray-200 bg-gray-50 hover:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100'
                  }`}
                  disabled={isSearching || isTotalMatched}
                />
                <Search className='absolute top-1/2 right-3 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400' />
              </div>
              
              {/* Date Filter Toggle Button */}
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setIsDateModalOpen(true)}
                  className={`p-2.5 sm:p-3 rounded-lg border-2 transition-all duration-200 shadow-sm flex items-center space-x-2 ${
                    startDate || endDate 
                      ? 'border-red-500 bg-red-50 text-red-600' 
                      : 'border-gray-200 bg-gray-50 hover:bg-white hover:border-red-500 text-gray-400'
                  }`}
                  title="Filter by date range"
                >
                  <Calendar className="h-5 w-5" />
                  {(startDate || endDate) && (
                    <span className="text-xs font-semibold hidden sm:inline">
                      {startDate ? format(startDate, 'MMM d') : ''} 
                      {startDate && endDate ? ' - ' : ''} 
                      {endDate ? format(endDate, 'MMM d') : ''}
                    </span>
                  )}
                </button>
                
                {(startDate || endDate) && (
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate(null);
                      setEndDate(null);
                      // Trigger search with nulled dates
                      handleSearchOrder({ startDate: null, endDate: null });
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    title="Clear dates"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <button
                onClick={handleSearchOrder}
                disabled={isSearching || !searchOrderId.trim() || (isTotalMatched && !isProcessed)}
                className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-xs sm:text-sm tracking-wide transition-all duration-300 whitespace-nowrap ${
                  searchOrderId.trim() && !isSearching && (!isTotalMatched || isProcessed)
                    ? 'bg-red-gradient text-white hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] shadow-md'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSearching ? (
                  <span className='flex items-center justify-center'>
                    <svg
                      className='animate-spin -ml-1 mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                    Searching...
                  </span>
                ) : (
                  'Search'
                )}
              </button>
            </div>
          </div>
          )}

          {/* Search Results Section - Orders matching the name */}
          {!isProcessed && (searchResults.length > 0 || isLoadingResults) && (
            <div className='px-3 sm:px-5 mb-4 sm:mb-6'>
              <div className='bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-4 sm:p-5 shadow-sm'>
                <button
                  type='button'
                  onClick={() => setIsSearchResultsExpanded(!isSearchResultsExpanded)}
                  className='w-full flex items-center justify-between cursor-pointer'
                >
                  <h3 className='text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2'>
                    <Search className='h-4 w-4 sm:h-5 sm:w-5 text-orange-600' />
                    Orders found ({searchResults.length})
                  </h3>
                  {isSearchResultsExpanded ? (
                    <ChevronUp className='h-5 w-5 text-gray-500' />
                  ) : (
                    <ChevronDown className='h-5 w-5 text-gray-500' />
                  )}
                </button>
                {isLoadingResults ? (
                  <div className='flex items-center justify-center py-6'>
                    <svg
                      className='animate-spin h-6 w-6 text-orange-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                      <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                    </svg>
                    <span className='ml-2 text-sm text-gray-600'>Searching orders...</span>
                  </div>
                ) : isSearchResultsExpanded ? (
                  <div className='space-y-2 max-h-60 overflow-y-auto mt-3'>
                    {searchResults.map((order) => {
                      const orderId = order?.payment_id;
                      const isAlreadyAdded = mappedOrders.some(
                        (o) => (o?.payment_info?.payment_id) === orderId
                      );
                      const isPreviouslyMapped = (zellePaymentDetails?.mapped_orders || []).some(
                        (o) => o.payment_id === orderId
                      );
                      const isPaymentPending = order?.payment_status === 'pending';
                      const isDisabled = isAlreadyAdded || isPreviouslyMapped || isTotalMatched || !isPaymentPending;

                      return (
                        <div
                          key={orderId}
                          className={`flex items-center justify-between bg-white border rounded-lg p-3 sm:p-4 transition-all ${
                            isDisabled ? 'opacity-60 border-gray-100' : 'border-gray-200 hover:border-orange-300 hover:shadow-sm'
                          }`}
                        >
                          <div className='grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 flex-1 min-w-0'>
                            <div>
                              <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                                Order ID
                              </label>
                              <p className='text-sm sm:text-base font-bold text-primary-100'>
                                #{orderId}
                              </p>
                            </div>
                            <div>
                              <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                                Name
                              </label>
                              <p className='text-xs sm:text-sm font-medium text-gray-900 truncate'>
                                {order?.customer_name || 'N/A'}
                              </p>
                              <div className='flex items-center'> 
                              <p className='text-xs sm:text-sm font-medium text-gray-900 truncate'>
                                {order?.formatted_phone_number || 'N/A'}
                              </p>
                              </div>
                            </div>
                            <div>
                              <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                                Total
                              </label>
                              <p className='text-sm sm:text-base font-bold text-red-700'>
                                {formatPrice(order?.total_amount)}
                              </p>
                            </div>
                            <div>
                              <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                                Payment
                              </label>
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium capitalize ${
                                order?.payment_status === 'paid' || order?.payment_status === 'completed'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {order?.payment_type || 'N/A'}
                              </span>
                            </div>
                            <div>
                              <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5'>
                                Order Status
                              </label>
                              <span className='inline-block px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-800'>
                                {order?.order_status_label || 'N/A'}
                              </span>
                            </div>
                          </div>
                          <button    
                            onClick={() => handleAddFromResults(order)}
                            disabled={isDisabled}
                            className={`ml-3 flex-shrink-0 p-2 rounded-full transition-all ${
                              isDisabled
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-green-600 hover:bg-green-50 hover:text-green-700'
                            }`}
                            title={
                              isPreviouslyMapped
                                ? 'Already mapped'
                                : isAlreadyAdded
                                ? 'Already added'
                                : isTotalMatched
                                ? 'Total matched'
                                : !isPaymentPending
                                ? 'Payment is not pending'
                                : 'Add order'
                            }
                          >
                            {isAlreadyAdded || isPreviouslyMapped ? (
                              <CheckCircle2 size={20} className='sm:w-[22px] sm:h-[22px]' />
                            ) : order?.payment_status === 'pending' ? (
                              <Plus size={20} className='sm:w-[22px] sm:h-[22px]' />
                            ) : null}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Mapped Orders List - Hide when processed */}
          {!isProcessed && (
          <div className='flex-1 overflow-y-auto px-3 sm:px-5'>
            {mappedOrders.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-8 sm:py-12 text-gray-400'>
                <Search size={40} className='sm:w-12 sm:h-12 mb-3 opacity-50' />
                <p className='text-base sm:text-lg font-medium text-gray-500'>No orders added yet</p>
                <p className='text-xs sm:text-sm text-gray-400 mt-1 text-center px-4'>
                  Select orders from the results above or search by Order ID
                </p>
              </div>
            ) : (
              <div className='space-y-3 sm:space-y-4 mb-4 sm:mb-6'>
                {mappedOrders.map((order) => {
                  const partnerInfo = getPartnerInfo(order.partner_info);
                  return (
                    <div
                      key={order?.payment_info?.payment_id}
                      className='relative bg-white border-2 border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow p-3 sm:p-5'
                    >
                      {/* Delete Button */}
                      <button
                        onClick={() => handleDeleteOrder(order?.payment_info?.payment_id)}
                        className='absolute top-0 right-0 sm:top-3 sm:right-3 p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors z-10'
                        title='Remove order'
                      >
                        <Trash2 size={16} className='sm:w-[18px] sm:h-[18px]' />
                      </button>

                      {/* Order Details */}
                      <div className='grid grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4 pr-8 sm:pr-10'>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Order ID
                          </label>
                          <a className='text-sm sm:text-base font-bold text-primary-100 cursor-pointer'>
                            #{order?.payment_info?.payment_id}
                          </a>
                        </div>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Name
                          </label>
                          <p className='text-sm sm:text-base font-medium text-gray-900 truncate'>
                            {order.order_details?.name || 'N/A'}
                          </p>
                        </div>

                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Phone Number
                          </label>
                          <p className='text-xs sm:text-sm text-gray-900'>
                            {order.order_details?.phone_number || order.order_details?.formatted_phone_number || 'N/A'}
                          </p>
                        </div>

                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Order Total
                          </label>
                          <p className='text-sm sm:text-md font-bold text-red-700'>
                            {order?.payment_info?.formatted_total_paid}
                          </p>
                        </div>

                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Order Type
                          </label>
                          <span
                            className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                              order.order_type === 'delivery-partner'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {order?.order_details?.order_type}
                          </span>
                        </div>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Payment Status
                          </label>
                          <span
                            className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                              order.payment_info?.payment_status === 'paid' || order.payment_info?.payment_status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : order.payment_info?.payment_status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {order.payment_info?.payment_status || 'N/A'}
                          </span>
                        </div>

                        {partnerInfo && (
                          <div className='col-span-2'>
                            <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                              Partner Info
                            </label>
                            <div className='bg-gray-50 p-2 sm:p-3 rounded-md border border-gray-200'>
                              <p className='text-xs sm:text-sm font-medium text-gray-900'>
                                {partnerInfo.name}
                              </p>
                              <p className='text-[10px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1'>{partnerInfo.address}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}
</div>
          {/* Footer - Hide when processed */}
          {!isProcessed && (
          <div className='border-t border-gray-200 p-3 sm:p-5 mt-3 sm:mt-4'>
            <div className='flex items-center justify-between mb-3 sm:mb-4'>
              <p className='text-xs sm:text-sm letter-spacing-07 text-gray-600'>
                {mappedOrders.length} order{mappedOrders.length !== 1 ? 's' : ''} added
              </p>
              {mappedOrders.length > 0 && (
                <div className='text-right'>
                  <p className='text-xs sm:text-sm text-gray-500'>
                    New Orders Total: <span className='font-bold text-gray-900'>{formatPrice(newlyMappedOrdersTotal)}</span>
                  </p>
                  {previouslyMappedTotal > 0 && (
                    <p className='text-xs sm:text-sm text-gray-500'>
                      Previously Mapped: <span className='font-bold text-gray-900'>{formatPrice(previouslyMappedTotal)}</span>
                    </p>
                  )}
                  <p className='text-xs sm:text-sm text-gray-500'>
                    Available Balance: <span className='font-bold text-gray-900'>{formatPrice(availableZelleBalance)}</span>
                  </p>
                  {newlyMappedOrdersTotal !== availableZelleBalance && (
                    <p className={`text-xs sm:text-sm font-semibold mt-1 ${
                      newlyMappedOrdersTotal > availableZelleBalance ? 'text-red-600' : 'text-orange-600'
                    }`}>
                      {newlyMappedOrdersTotal > availableZelleBalance
                        ? `Exceeds by ${formatPrice(newlyMappedOrdersTotal - availableZelleBalance)}`
                        : `Short by ${formatPrice(availableZelleBalance - newlyMappedOrdersTotal)}`
                      }
                    </p>
                  )}
                  {newlyMappedOrdersTotal === availableZelleBalance && (
                    <p className='text-xs sm:text-sm font-semibold text-green-600 mt-1'>
                      ✓ Amounts Match
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className='flex justify-center'>
              <button
                onClick={handleApply}
                disabled={mappedOrders.length === 0 || isApplying}
                className={`w-full sm:w-3/5 py-2.5 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-colors ${
                  mappedOrders.length === 0 || isApplying
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'main-btn hover:opacity-90'
                }`}
              >
                {isApplying ? 'Applying...' : 'Apply Mapping'}
              </button>
            </div>
          </div>
          )}
          <UnmapConfirmModal
            open={isUnmapConfirmOpen}
            setOpen={setIsUnmapConfirmOpen}
            order={orderToUnmap}
            onConfirm={handleUnmapOrder}
            isUnmapping={isUnmapping}
          />
          <DateRangeModal
            open={isDateModalOpen}
            setOpen={setIsDateModalOpen}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            onApply={(dates) => handleSearchOrder(dates)}
          />
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// Date Range Selection Sub-modal
const DateRangeModal = ({ open, setOpen, startDate, setStartDate, endDate, setEndDate, onApply }) => {
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);

  // Sync temp dates when modal opens
  useEffect(() => {
    if (open) {
      setTempStart(startDate);
      setTempEnd(endDate);
    }
  }, [open, startDate, endDate]);

  const handleApply = () => {
    setStartDate(tempStart);
    setEndDate(tempEnd);
    if (onApply) onApply({ startDate: tempStart, endDate: tempEnd });
    setOpen(false);
  };

  const handleClear = () => {
    setTempStart(null);
    setTempEnd(null);
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} className='relative z-[60]'>
      <DialogBackdrop className='fixed inset-0 bg-black/40 backdrop-blur-sm' />

      <div className='fixed inset-0 z-[60] flex items-center justify-center p-4'>
        <DialogPanel className='bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200'>
          <div className="flex items-center justify-between mb-6">
            <DialogTitle className='text-lg font-bold text-gray-900 flex items-center gap-2'>
              <Calendar className="h-5 w-5 text-red-500" />
              Select Date Range
            </DialogTitle>
            <button
              onClick={() => setOpen(false)}
              className='text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 p-1 transition-colors'
            >
              <X size={20} />
            </button>
          </div>

          <div className="space-y-6 w-full">
            <div className='w-full'>
              <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                From Date
              </label>
              <div className="relative w-full">
                <DatePicker
                  selected={tempStart}
                  onChange={(date) => setTempStart(date)}
                  selectsStart
                  startDate={tempStart}
                  endDate={tempEnd}
                  placeholderText="Select start date"
                  className="w-full border-2 border-gray-100 bg-gray-50 rounded-xl py-3 px-4 text-sm focus:outline-none focus:border-red-500 focus:bg-white transition-all"
                  wrapperClassName="w-full"
                  isClearable
                />
              </div>
            </div>

            <div className='w-full'>
              <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                To Date
              </label>
              <div className="relative w-full">
                <DatePicker
                  selected={tempEnd}
                  onChange={(date) => setTempEnd(date)}
                  selectsEnd
                  startDate={tempStart}
                  endDate={tempEnd}
                  minDate={tempStart}
                  placeholderText="Select end date"
                  className="w-full border-2 border-gray-100 bg-gray-50 rounded-xl py-3 px-4 text-sm focus:outline-none focus:border-red-500 focus:bg-white transition-all"
                  wrapperClassName="w-full"
                  isClearable
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-8">
            <button
              onClick={handleClear}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="flex-[2] px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-gradient hover:shadow-lg transition-all"
            >
              Apply Filter
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// Confirmation modal for unmapping
const UnmapConfirmModal = ({ open, setOpen, order, onConfirm, isUnmapping }) => {
  return (
    <Dialog open={open} onClose={() => !isUnmapping && setOpen(false)} className='relative z-[70]'>
      <DialogBackdrop className='fixed inset-0 bg-black/40 backdrop-blur-sm' />
      <div className='fixed inset-0 z-[70] flex items-center justify-center p-4'>
        <DialogPanel className='bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200'>
          <div className='flex flex-col items-center text-center'>
            <div className='w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mb-4'>
              <AlertCircle size={30} className='text-red-500' />
            </div>
            <DialogTitle className='text-lg font-bold text-gray-900 mb-2'>
              Unmap Order #{order?.payment_id}?
            </DialogTitle>
            <p className='text-sm text-gray-500 mb-6'>
              Are you sure you want to remove the mapping for this order? This action will restore the available balance for this Zelle payment.
            </p>
            
            <div className='flex items-center gap-3 w-full'>
              <button
                onClick={() => setOpen(false)}
                disabled={isUnmapping}
                className='flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isUnmapping}
                className='flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-gradient hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70'
              >
                {isUnmapping ? (
                  <>
                    <svg className='animate-spin h-4 w-4 text-white' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                      <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                    </svg>
                    <span>Unmapping...</span>
                  </>
                ) : (
                  'Yes, Unmap'
                )}
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default OrderMappingModal;
